import {AssetType, DocumentType, PartyRole, PayoutType} from '@sollapay/enums';

import type {TrustPurpose} from '@sollapay/enums';
import type {Money} from '@sollapay/utils/money';

/** Payout types that name the same destination as another, mapped to the one stored and offered. */
const PAYOUT_TYPE_ALIASES: Partial<Record<PayoutType, PayoutType>> = {
  [PayoutType.DEVELOPER_BANK_ACCOUNT]: PayoutType.SELLER_BANK_ACCOUNT
};

/**
 * Maps each payout type to the canonical value for its destination, dropping duplicates, so a
 * client that sends (or a row that stores) either name reads as the same selected option.
 */
export function normalizePayoutTypes(types?: PayoutType[] | null): PayoutType[] {
  return [...new Set((types ?? []).map(type => PAYOUT_TYPE_ALIASES[type] ?? type))];
}

/** A scope-step field a deal type can require. Widen as more per-type scope fields appear. */
export type TrustScopeRequiredField = 'transactionType';

/** Inputs for the per-type "amount due" figure shown against a buyer's purchase. */
export interface AmountDueParams {
  /** Total collectable for the purchase — `purchasePrice × maxCollectionPercentage`. */
  maxCollectable: Money;
  /** Sum already settled against the purchase. */
  depositPaid: Money;
}

/**
 * Per-`TrustPurpose` encapsulation of behaviour that differs by trust type. Each trust type
 * owns its own rules — no `if (trustPurpose === X)` branches should exist outside this folder.
 * Adding a new trust type means adding one class implementing this interface and one entry in
 * `trust-type.registry.ts`; the registry's exhaustive `Record` makes a missing entry a build error.
 */
export interface TrustTypeStrategy {
  /** The `TrustPurpose` this strategy implements, or `null` for the no-purpose-set default. */
  readonly purpose: TrustPurpose | null;

  /** Maximum percentage of the purchase price that can be collected as payment instructions. */
  readonly maxCollectionPercentage: number;

  /**
   * Whether the UI should surface the collection cap anywhere — the "(7%)" amount-due hint, the max-collection metric.
   * False once collection isn't capped below the full price — showing "(100%)" would be noise.
   */
  readonly showsCollectionCapHint: boolean;

  /**
   * Whether `maxCollectionPercentage` is enforced as a hard ceiling when a payment instruction is
   * created, or is only a reference figure for the "amount due" display. Distinct from
   * `maxCollectionPercentage` itself, which always states the obligation regardless of enforcement —
   * false for general sale and options, where the asset price must stay visible without capping
   * what a buyer can be asked to pay in.
   */
  readonly enforcesCollectionCap: boolean;

  /**
   * Party roles a new buyer receives under this trust type (states the full set, not an
   * addition to a base — always includes `BUYER`).
   */
  getBuyerPartyRoles(): PartyRole[];

  /** The side disposing of the asset: a developer in a developer sale, a seller in a general one. */
  readonly sellerSideRole: PartyRole;

  /** Full role set for that party, like `getBuyerPartyRoles`. */
  getSellerSidePartyRoles(sellerRole?: PartyRole | null): PartyRole[];

  /** Whether a roster row states its own seller-side role, rather than inheriting the default.
   * Derived from `getSellerSideRoleOptions()` — true exactly when it offers more than one. */
  readonly asksSellerSideRole: boolean;

  /** The roles a roster row may take when it is asked. */
  getSellerSideRoleOptions(): PartyRole[];

  /** The role to store for a roster row: null whenever the deal type derives it instead. */
  resolveSellerRole(sellerRole?: PartyRole | null): PartyRole | null;

  /**
   * The "amount due" figure for a purchase. Distinct from payment *status*, which always
   * measures paid against the full obligation (`maxCollectable`) regardless of this figure.
   */
  computeAmountDue(params: AmountDueParams): Money;

  /** Foundation documents that must be uploaded before the compliance package is ready. */
  getRequiredComplianceDocumentTypes(): DocumentType[];

  /** Per-buyer documents a purchase needs — the deal's own agreement. */
  getRequiredPurchaseDocumentTypes(): DocumentType[];

  /** Additional per-buyer upload slots offered but never required. Empty by default. */
  getOptionalPurchaseDocumentTypes(): DocumentType[];

  /** Asset types a purchase may be created against under this trust type. */
  getAllowedAssetTypes(): AssetType[];

  /**
   * Developer sale holds many units; general sale and options hold exactly one asset. Also drives whether
   * the activity feed and transactions tab name the asset next to each row — pointless when
   * every row shares the account's one asset.
   */
  readonly allowsMultipleAssets: boolean;

  /**
   * Whether the account's single purchase is created up front, when activation is approved, rather
   * than by the first Add Buyer. True only where the deal is one contract over one property, so the
   * purchase is knowable before any buyer exists and is priced from the asset's reference price.
   */
  readonly createsPurchaseAtActivation: boolean;

  /**
   * Whether the account can hold more than one purchase. False means every buyer joins the same
   * one. Distinct from `allowsMultipleAssets`: an option assignment has a single shared land parcel
   * (one asset) but a separate purchase per assignment agreement.
   *
   * Also decides how the buyers table groups rows, because the two follow from each other: where
   * purchases are many a row *is* a purchase, so one Add Buyer action's co-buyers share it; where
   * there is only one, a shared row would collapse the whole account into a single line, so rows
   * split per buyer instead.
   */
  readonly allowsMultiplePurchases: boolean;

  /**
   * Whether the create-purchase path (`POST /:id/buyers`) asks the lawyer to describe the asset.
   * False for option assignment and options: the parcel is already described once on the
   * activation asset, so re-collecting it per assignment would be noise. Option assignment's asset
   * carries no descriptor fields and is generated with a sequential display code instead. Second-hand never reaches that path
   * (it adds buyers through `addToPurchase`), so its answer is moot; how many purchases an account
   * may hold is `allowsMultiplePurchases`, enforced on its own by `PurchasePolicy`.
   */
  readonly collectsAssetPerPurchase: boolean;

  /**
   * Whether one buyer may hold several purchases on the same account — a developer-sale buyer can
   * buy two units. False for option assignment, where a second purchase for the same buyer is
   * always a duplicate submission rather than a second agreement.
   */
  readonly allowsBuyerOnMultiplePurchases: boolean;

  /**
   * Whether removing a purchase's last buyer deletes the purchase and its asset. True for option
   * assignment, whose purchase and generated asset exist only for that buyer group; elsewhere a
   * purchase with no buyers is a normal, standing state.
   */
  readonly deletesPurchaseWithLastBuyer: boolean;

  /** Whether a trust account can hold more than one selling-side party. True for general sale
   * (co-owners, heirs, or a mixed individual/company group selling one asset) and for options
   * (land owners and option holders); false otherwise. */
  readonly allowsMultipleSellerSideParties: boolean;

  /**
   * Whether the relationship-diligence questions (`relationshipType`, `metFaceToFace`) apply to
   * this trust type's selling side. True for seven-percent (the firm's ongoing relationship with
   * the developer) and false for general sale, where the "seller" is a one-off counterparty the
   * firm has no ongoing relationship with — the questions don't apply and aren't asked or scored.
   */
  readonly asksSellerSideRelationshipQuestions: boolean;

  /**
   * Roles the `הוסף צד` inline-row flow may add to the beneficiaries table for this trust
   * type. Empty unless the deal type pays parties added inline — general sale's supplier/trustee.
   */
  readonly inlineAddableBeneficiaryRoles: PartyRole[];

  /**
   * Whether the beneficiaries table footer shows an add-party CTA when
   * `inlineAddableBeneficiaryRoles` is empty (the legacy "add buyer" flow). False for
   * seven-percent, where the beneficiaries table doesn't offer an add action at all.
   */
  readonly showsBeneficiaryAddCta: boolean;

  /** Whether the trust account view shows a Payouts tab and quick action — true for general sale and options. */
  readonly showsPayoutsTab: boolean;

  /** Whether buyers surface the tax-authority reporting badge — false for option assignment. */
  readonly showsRegulatoryReports: boolean;

  /**
   * Whether a buyer's page lists only that buyer's own payments. True where co-buyers on a shared
   * purchase each pay separately (general sale, options); false for seven-percent, whose history
   * is deliberately shared across the co-buyers of a unit.
   */
  readonly scopesPaymentHistoryToBuyer: boolean;

  /** Scope-step fields this deal type must have answered before its package is ready. */
  getRequiredScopeFields(): TrustScopeRequiredField[];

  /** Whether the account's one shared asset must be defined (via `PUT .../asset`) before the
   * trust-details package is ready. False for developer sale, whose assets are per-purchase. */
  readonly requiresAssetAtActivation: boolean;

  /** Pre-selected in the asset step when nothing is saved yet — null when the type list has no
   * natural default (the lawyer must choose explicitly). */
  getDefaultAssetType(): AssetType | null;

  /** Document types offered for upload at the asset step itself (distinct from
   * `getAllPurchaseDocumentTypes()`, which is the per-buyer-group document set). Empty for
   * developer sale, which has no asset step. */
  getAssetPropertyDocumentTypes(): DocumentType[];

  /** Additional asset-step upload slots offered but never gated on — a supplementary document a
   * deal type accepts without requiring. Empty by default. */
  getOptionalAssetPropertyDocumentTypes(): DocumentType[];

  /** Payout destinations this deal type offers, in display order. */
  getPayoutTypeOptions(): PayoutType[];

  /** Keeps only the destinations this deal type offers — a stale selection can never persist. */
  resolvePayoutTypes(types?: PayoutType[] | null): PayoutType[];
}

/**
 * The conventional deal shape: a capped deposit is collected so the amount due is the whole
 * obligation, land ownership is the only foundation document, and both purchase document types
 * are accepted. Subclasses state their own purpose, cap and party roles; a type that departs
 * from this shape overrides only the member in question.
 */
export abstract class StandardTrustType implements TrustTypeStrategy {
  abstract readonly purpose: TrustPurpose | null;
  abstract readonly maxCollectionPercentage: number;

  get showsCollectionCapHint(): boolean {
    return this.maxCollectionPercentage < 100;
  }

  readonly enforcesCollectionCap: boolean = true;

  abstract getBuyerPartyRoles(): PartyRole[];

  readonly sellerSideRole: PartyRole = PartyRole.DEVELOPER;

  getSellerSidePartyRoles(sellerRole?: PartyRole | null): PartyRole[] {
    return [this.resolveSellerRole(sellerRole) ?? this.sellerSideRole, PartyRole.BENEFICIARY];
  }

  /** Asked only where there's a real choice — a single-option list has nothing to ask about. */
  get asksSellerSideRole(): boolean {
    return this.getSellerSideRoleOptions().length > 1;
  }

  getSellerSideRoleOptions(): PartyRole[] {
    return [this.sellerSideRole];
  }

  resolveSellerRole(sellerRole?: PartyRole | null): PartyRole | null {
    if (!this.asksSellerSideRole || !sellerRole) return null;
    return this.getSellerSideRoleOptions().includes(sellerRole) ? sellerRole : null;
  }

  computeAmountDue({maxCollectable}: AmountDueParams): Money {
    return maxCollectable;
  }

  getRequiredComplianceDocumentTypes(): DocumentType[] {
    return [DocumentType.LAND_OWNERSHIP_VERIFICATION];
  }

  getRequiredPurchaseDocumentTypes(): DocumentType[] {
    return [DocumentType.SIGNED_SALE_AGREEMENT];
  }

  getOptionalPurchaseDocumentTypes(): DocumentType[] {
    return [DocumentType.BUYER_REGISTRATION_FORM];
  }

  readonly allowsMultipleAssets: boolean = true;

  /** Only a one-contract-one-property deal knows its purchase before a buyer exists. */
  readonly createsPurchaseAtActivation: boolean = false;

  readonly allowsMultiplePurchases: boolean = true;

  readonly collectsAssetPerPurchase: boolean = true;

  readonly allowsBuyerOnMultiplePurchases: boolean = true;

  readonly deletesPurchaseWithLastBuyer: boolean = false;

  readonly allowsMultipleSellerSideParties: boolean = false;

  readonly asksSellerSideRelationshipQuestions: boolean = true;

  readonly inlineAddableBeneficiaryRoles: PartyRole[] = [];

  readonly showsBeneficiaryAddCta: boolean = true;

  readonly showsPayoutsTab: boolean = false;

  readonly showsRegulatoryReports: boolean = true;

  readonly scopesPaymentHistoryToBuyer: boolean = false;

  getRequiredScopeFields(): TrustScopeRequiredField[] {
    return [];
  }

  readonly requiresAssetAtActivation: boolean = false;

  getAllowedAssetTypes(): AssetType[] {
    return [AssetType.DEVELOPER_UNIT];
  }

  getDefaultAssetType(): AssetType | null {
    return null;
  }

  getAssetPropertyDocumentTypes(): DocumentType[] {
    return [];
  }

  getOptionalAssetPropertyDocumentTypes(): DocumentType[] {
    return [];
  }

  getPayoutTypeOptions(): PayoutType[] {
    return [PayoutType.SELLER_BANK_ACCOUNT, PayoutType.FUNDED_BANK, PayoutType.OTHER];
  }

  resolvePayoutTypes(types?: PayoutType[] | null): PayoutType[] {
    const offered = new Set(this.getPayoutTypeOptions());
    return normalizePayoutTypes(types).filter(type => offered.has(type));
  }
}

/** Every asset-step upload slot a strategy offers, required and optional together — the shape
 * every caller that just wants "what can be uploaded here" actually needs. */
export function getAllAssetPropertyDocumentTypes(strategy: TrustTypeStrategy): DocumentType[] {
  return [
    ...strategy.getAssetPropertyDocumentTypes(),
    ...strategy.getOptionalAssetPropertyDocumentTypes()
  ];
}

/** Every per-buyer upload slot a strategy offers, required and optional together. */
export function getAllPurchaseDocumentTypes(strategy: TrustTypeStrategy): DocumentType[] {
  return [
    ...strategy.getRequiredPurchaseDocumentTypes(),
    ...strategy.getOptionalPurchaseDocumentTypes()
  ];
}
